import type { PTZ } from '@studio/models/ptz';
import { AmbitAll, Movement } from '@studio/models/ptz';
import { useLatestFn, useLazyRef } from '@studio/react-utils/hooks';
import type { ReactElement } from 'react';
import { twMerge } from 'cn';
import type { Props } from './types.js';
import { Zoom } from './zoom.jsx';
import { locateProxy } from '#~/agent/index.js';
import { Choreographer, DPad } from '#~/control/components/d-pad/index.js';
import { Facet, Pace, Pipeline, Piston, Speeds, Throttle } from '#~/control/movement/index.js';

/** `<Navipad>` component. */
export const Navipad = ({
  className,
  deviceId,
  disabled,
  onMove,
  ...props
}: Props): ReactElement => {
  const forwardFn = useLatestFn((intent: PTZ) => {
    onMove?.(intent);
  });

  const agent = deviceId ? locateProxy(deviceId) : undefined;
  const joystickUpdateFrequency =
    deviceId && agent?.state.status?.device[deviceId]?.config?.joystickUpdateFrequency;

  const piston = useLazyRef(
    () =>
      new Piston(
        new Pipeline(
          new Throttle(AmbitAll, { limit: Speeds[Facet.PanTilt][Pace.Medium] }),
          forwardFn
        ),
        joystickUpdateFrequency ?? 10
      )
  ).current;

  const canPan = deviceId
    ? !!agent?.state.status?.device[deviceId]?.capabilities?.movement?.vectors?.pan?.absoluteMove
    : false;
  const canTilt = deviceId
    ? !!agent?.state.status?.device[deviceId]?.capabilities?.movement?.vectors?.tilt?.absoluteMove
    : false;

  const handleMove = useLatestFn((intent: PTZ) => {
    piston.process(intent);
  });

  return (
    <div className={twMerge('flex gap-16 rounded-8', className)} {...props}>
      <div className='flex h-[231px] grow justify-center bg-base-300 p-16'>
        <DPad
          deviceId={deviceId}
          canPan={canPan}
          canTilt={canTilt}
          disabled={disabled}
          onMove={handleMove}
        />
      </div>

      <Choreographer
        deviceId={deviceId}
        className='flex flex-col gap-16 self-start rounded-8 bg-base-300 p-16'
        onMove={handleMove}
      >
        <Zoom disabled={disabled} icon='zoom-in' movement={Movement.ZoomIn} />
        <Zoom disabled={disabled} icon='zoom-out' movement={Movement.ZoomOut} />
      </Choreographer>
    </div>
  );
};
